import styled from 'styled-components';

import { Loading, Typography } from 'components';
import { Icon } from 'components/icon';
import { formatDate, stringfyArrayItems } from 'app/utils';
import { AnalysisReportStatusEnum, useReports } from 'features/reports';
import { IconsTypes } from 'components/icons';

interface AnalysisReportProps {
  typeName: string;
  date: string;
  providerName: string;
  name: string;
  status: AnalysisReportStatusEnum;
  downloadFilename: string;
  uuid?: string;
  buttonLabel: string;
  buttonIcon: IconsTypes;
  allowedCourses: string[];
  image?: string;
  theme: 'light' | 'dark';
}

const CardSiding = styled.div`
  display: none;
  position: absolute;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.black80};
  z-index: 10;
  border-radius: 3px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  padding: 1rem;
`;

const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem;
`;

const CardTypeWrapper = styled.div``;

const CardDate = styled.div`
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 400;
  margin-top: 4px;
`;

const CardTypeName = styled.div`
  font-size: 1rem;
  line-height: 1.125rem;
  font-weight: 700;
`;

const CardIconWrapper = styled.div`
  display: flex;
  width: 24px;
`;

const CardIcon = styled(Icon)`
  width: 24px;
`;

const CardProviderName = styled.div`
  font-size: 0.75rem;
  line-height: 0.875rem;
  font-weight: 700;
  text-transform: uppercase;
`;

const CardNameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
`;

const CardName = styled.div`
  font-size: 1.5rem;
  line-height: 1.75rem;
  font-weight: 700;
  margin-top: 4px;
`;

const CardButtonIcon = styled(Icon)`
  display: flex;
  width: 20px;
`;

const CardButtonLabel = styled.div`
  font-size: 1rem;
  line-height: 1;
  font-weight: 700;
  text-transform: uppercase;
`;

const CardButton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom-left-radius: 3px;
  border-bottom-right-radius: 3px;
`;

const CardFoot = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardWrapper = styled.div`
  width: 280px;
  height: 340px;
  border-radius: 4px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: ${(props: any) => (props['data-isavailable'] ? 'pointer' : 'inherit')};
  position: relative;

  background-color: ${(props: any) => (props['data-hasimage'] ? 'black' : 'white')};
  ${(props: any) => {
    if (props['data-hasimage']) {
      return `
        background:
          linear-gradient(180deg, rgba(29, 31, 30, 0.8) 7.94%, rgba(29, 31, 30, 0) 32.65%),
          linear-gradient(180deg, rgba(29, 31, 30, 0) 45.52%, rgba(29, 31, 30, 0.8) 86.14%),
          url(${props['data-image']});
        background-size: cover;
        background-position: top center;
      `;
    }
  }}

  border: ${(props: any) => (props['data-hasimage'] ? '0' : `1px solid ${props.theme.colors.gray_100}`)};
  border-radius: 4px;

  ${CardTypeName} {
    color: ${(props: any) => (props['data-hasimage'] ? 'white' : props.theme.colors.black80)};
  }

  ${CardDate} {
    color: ${(props: any) => (props['data-hasimage'] ? 'white' : props.theme.colors.black50)};
  }

  ${CardProviderName} {
    color: ${(props: any) => props.theme.colors.primary_400};
  }

  ${CardName} {
    color: ${(props: any) => (props['data-hasimage'] ? 'white' : props.theme.colors.black80)};
  }

  ${CardIcon} {
    color: ${(props: any) => (props['data-hasimage'] ? 'white' : props.theme.colors.black30)};
  }

  ${CardButtonIcon} {
    color: ${(props: any) => (props['data-isavailable'] ? props.theme.colors.primary_400 : props.theme.colors.black30)};
  }

  ${CardButtonLabel} {
    color: ${(props: any) => (props['data-isavailable'] ? props.theme.colors.primary_400 : props.theme.colors.black30)};
  }

  ${CardButton} {
    background: ${(props: any) => props.theme.colors.gray_100};
  }

  &:hover {
    border-color: ${(props: any) => (props['data-isavailable'] ? props.theme.colors.primary_400 : props.theme.colors.gray_100)};

    ${(props: any) => {
      if (props['data-isavailable']) {
        return `
          box-shadow: 0px 4px 8px ${props.theme.colors.black20};
        `;
      }
    }}

    ${CardButtonIcon} {
      color: ${(props: any) => (props['data-isavailable'] ? props.theme.colors.primary_contrast : props.theme.colors.black30)};
    }

    ${CardButtonLabel} {
      color: ${(props: any) => (props['data-isavailable'] ? props.theme.colors.primary_contrast : props.theme.colors.black30)};
    }

    ${CardButton} {
      background: ${(props: any) => (props['data-isavailable'] ? props.theme.colors.primary_400 : props.theme.colors.gray_100)};
    }

    ${CardSiding} {
      display: flex;
    }
  }
`;

const AnalysisReport = ({
  status,
  uuid,
  downloadFilename,
  buttonIcon,
  buttonLabel,
  typeName,
  date,
  providerName,
  name,
  image,
  allowedCourses,
}: AnalysisReportProps) => {
  const { downloadLoading, downloadPercentage, downloadReport } = useReports();

  const isAvailable = status === AnalysisReportStatusEnum.Available;

  return (
    <>
      <CardWrapper
        onClick={() => {
          if (isAvailable && uuid) {
            if (downloadReport) downloadReport(uuid, downloadFilename);
          }
        }}
        data-hasimage={!!image}
        data-image={image}
        data-isavailable={isAvailable}
      >
        {status === AnalysisReportStatusEnum.Unavailable && !!allowedCourses.length && (
          <CardSiding>
            <Typography as="span" type="type2" color="white" fontWeight="bold" upper>
              Disponível para:
            </Typography>

            <Typography as="span" type="type1" color="white" lineHeight={1.3} mt={2}>
              {stringfyArrayItems(allowedCourses)}
            </Typography>
          </CardSiding>
        )}

        {downloadLoading && (
          <Loading fixed={false} bordered alpha={0.8} label={`Por favor, aguarde.<br />${downloadPercentage ? `${downloadPercentage}%` : ``}`} />
        )}

        <CardHead>
          <CardTypeWrapper>
            <CardTypeName>{typeName}</CardTypeName>
            <CardDate>{formatDate(date).format('DD [de] MMMM [de] YYYY')}</CardDate>
          </CardTypeWrapper>

          <CardIconWrapper>
            <CardIcon icon="textBoxOutline" />
          </CardIconWrapper>
        </CardHead>

        <CardFoot>
          <CardNameWrapper>
            <CardProviderName>{providerName}</CardProviderName>
            <CardName>{name}</CardName>
          </CardNameWrapper>

          <CardButton>
            <CardButtonLabel>{buttonLabel}</CardButtonLabel>
            <CardButtonIcon icon={buttonIcon} />
          </CardButton>
        </CardFoot>
      </CardWrapper>
    </>
  );
};

export default AnalysisReport;
